/**
 * Session cache warm-up. The product rule: once a client signs in, pull their whole
 * config ONCE and cache it (localStorage, via stores/cache.ts) so every screen opens
 * instantly and nothing has to reload on each visit. Server-side changes still flow in
 * because each surface revalidates in the background (useCachedResource, ~60s) and our
 * saves push the fresh value straight into the cache — so we can change a client's
 * settings/options "from our side" and it appears without them re-uploading anything.
 *
 * Reference images are CDN URLs (R2); we kick off a fetch for each so the browser/webview
 * HTTP cache has them ready too. Best-effort throughout: a failing/offline fetch never
 * throws and never blocks sign-in.
 */
import { cacheEpoch, writeCache } from "./cache.js";
import { setSegModel } from "./modelStore.js";
import type { Appearance, TemplateOptions, WorkspaceSettings } from "@genz/platform";

/** Cache keys shared by the warm-up and the cache-first surfaces that read them. */
export const CACHE_KEYS = {
  settings: "settings.workspace",
  templateOptions: "settings.template-options",
  appearance: "appearance.platform",
} as const;

/** Just the client surface the warm-up needs (keeps it unit-testable). */
export interface WarmupClient {
  getSettings(): Promise<WorkspaceSettings>;
  templateOptions(): Promise<TemplateOptions>;
  getAppearance(): Promise<Appearance>;
}

async function cacheInto<T>(key: string, fetch: () => Promise<T>): Promise<T | null> {
  const epoch = cacheEpoch();
  try {
    const v = await fetch();
    writeCache(key, v, epoch);
    return v;
  } catch {
    return null; // offline / transient — keep any prior cached value, never throw
  }
}

function preloadReferenceImages(opts: TemplateOptions | null): void {
  if (!opts || typeof Image === "undefined") return;
  for (const v of opts.views ?? []) {
    const ref = v.reference_ref;
    if (ref && /^https?:\/\//.test(ref)) {
      try { new Image().src = ref; } catch { /* preload is best-effort */ }
    }
  }
}

/**
 * Re-read the platform settings into the cache. A device that stays signed in never passes
 * the login warm-up again, so a calibration a super-admin saved elsewhere did not reach it
 * (owner, 2026-10-13: "it should be global for every account"). The shell calls this when it
 * opens on a session and each time the window comes back into view.
 */
export async function refreshSettingsCache(client: Pick<WarmupClient, "getSettings">): Promise<void> {
  await cacheInto(CACHE_KEYS.settings, () => client.getSettings());
}

/** Prefetch + cache the signed-in client's config. Call once, fire-and-forget, at login. */
export async function warmSessionCache(client: WarmupClient): Promise<void> {
  const [settings, opts] = await Promise.all([
    cacheInto(CACHE_KEYS.settings, () => client.getSettings()),
    cacheInto(CACHE_KEYS.templateOptions, () => client.templateOptions()),
    cacheInto(CACHE_KEYS.appearance, () => client.getAppearance()),
  ]);
  // Apply the super-admin's segmenter model choice so the right ONNX (BiRefNet by
  // default) is selected before the first auto-clean (spec 022). Best-effort.
  const model = settings?.segmenter_calibration?.model;
  if (model === "birefnet" || model === "birefnet_full" || model === "u2netp") setSegModel(model);
  preloadReferenceImages(opts);
}
